import React, { createContext, useContext, useMemo, useState } from 'react';
import { usePersistentState } from '../hooks/usePersistentState.js';

// Meals the user has actually made. An entry is written when they mark a
// recipe as made — there are no orders, carts or spend figures in this
// release, so an entry is just "what I cooked, when, and where I shopped".
// In-memory only; real persistence can replace the seed and the functions
// below without the screen changing.
//
// `recipeId` points into RecipeLibraryContext so "cook again" hands back the
// recipe actually used rather than re-deriving one from the name. `mealName`
// stays denormalised so this screen renders without a library lookup, and so
// an entry with no recipe behind it still displays.
//
// Persisted since Stage 5. `clearAll` writes an empty array rather than
// removing the record, so a history the user deliberately emptied stays empty
// instead of being re-seeded on the next launch.

const HistoryContext = createContext(null);
const WINDOW_DAYS = 90;

const SEED = [
  { id: 'h1', recipeId: 'salmon-rice-bowl', mealName: 'Grilled salmon rice bowl', completedAt: new Date('2026-09-01T18:20:00').getTime(), store: 'ALDI', rating: null },
  { id: 'h2', recipeId: 'beef-tacos', mealName: 'Weeknight beef tacos', completedAt: new Date('2026-08-22T17:45:00').getTime(), store: "Trader Joe's", rating: null },
  { id: 'h3', recipeId: 'veggie-pad-thai', mealName: 'Veggie pad thai', completedAt: new Date('2026-08-10T12:05:00').getTime(), store: 'ALDI', rating: null },
  { id: 'h4', recipeId: 'chicken-katsu-curry', mealName: 'Chicken katsu curry', completedAt: new Date('2026-07-28T19:15:00').getTime(), store: 'Kroger', rating: null },
  { id: 'h5', recipeId: 'margherita-pizza', mealName: 'Margherita pizza', completedAt: new Date('2026-07-14T18:00:00').getTime(), store: 'ALDI', rating: null },
  { id: 'h6', recipeId: 'shrimp-fried-rice', mealName: 'Shrimp fried rice', completedAt: new Date('2026-06-30T13:10:00').getTime(), store: "Trader Joe's", rating: null },
  { id: 'h7', recipeId: 'beef-chili', mealName: 'Classic beef chili', completedAt: new Date('2026-05-05T17:30:00').getTime(), store: 'ALDI', rating: null },
  { id: 'h8', recipeId: 'lemon-herb-chicken', mealName: 'Lemon herb chicken', completedAt: new Date('2026-04-18T18:50:00').getTime(), store: 'Kroger', rating: null },
];

export function HistoryProvider({ children }) {
  const [allEntries, setAllEntries] = usePersistentState('history', SEED);
  const [showAll, setShowAll] = useState(false);

  const value = useMemo(() => {
    const sorted = [...allEntries].sort((a, b) => b.completedAt - a.completedAt);
    const cutoff = Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const visible = showAll ? sorted : sorted.filter((entry) => entry.completedAt >= cutoff);
    const hasMore = !showAll && sorted.some((entry) => entry.completedAt < cutoff);

    // When each recipe was last cooked, across ALL history rather than the
    // 90-day display window — reuse shouldn't expire just because the entry
    // scrolled out of view. Recipe search ranks on this.
    const cookedAt = {};
    for (const entry of allEntries) {
      if (!entry.recipeId) continue;
      if (!cookedAt[entry.recipeId] || entry.completedAt > cookedAt[entry.recipeId]) {
        cookedAt[entry.recipeId] = entry.completedAt;
      }
    }

    return {
      entries: visible,
      totalCount: allEntries.length,
      hasMore,
      cookedAt,
      viewAll: () => setShowAll(true),

      // Called when a recipe is marked as made.
      addEntry: ({ mealName, store, recipeId = null }) =>
        setAllEntries((prev) => [
          {
            id: `h${Math.random().toString(36).slice(2, 9)}`,
            recipeId,
            mealName,
            completedAt: Date.now(),
            store: store || null,
            rating: null,
          },
          ...prev,
        ]),

      removeEntry: (id) => setAllEntries((prev) => prev.filter((entry) => entry.id !== id)),
      clearAll: () => setAllEntries([]),
    };
  }, [allEntries, showAll]);

  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
}

export function useHistory() {
  const context = useContext(HistoryContext);
  if (!context) throw new Error('useHistory must be used inside <HistoryProvider>');
  return context;
}
